import global from '@app/filters/global';
import { Map } from '@app/services/export/ppt/classes/Map';
import PowerPointSlideProcessor from '@app/services/export/ppt/slides/PowerPointSlideProcessor';
import {
	createBadgeForSlide,
	createLogoForSlide,
	getBadgeWidthFromText
} from '@app/services/export/ppt/utils/layoutUtils';
import {
	colors,
	defaultViewPortPadding,
	elementSizes,
	presentationDimensions
} from '@app/services/export/ppt/variables/constants';
import { copyBold, copySmall, h3 } from '@app/services/export/ppt/variables/typography';
import ISkiRegionSlideData from '@app/types/data/slides/ISkiRegionSlideData';
import PptxGenJS from 'pptxgenjs';

import TextProps = PptxGenJS.TextProps;

export default class PowerPointSkiRegionSlideProcessor extends PowerPointSlideProcessor<ISkiRegionSlideData> {
	public async processSlideData(): Promise<void> {
		const slide: PptxGenJS.Slide = this.presentation.addSlide();
		const overlayHeight = 1.2;
		const textArray: TextProps[] = [];
		const upperMetaDataText = [];
		const lowerMetaDataText = [];

		// map
		if (this.slideData.image) {
			const map = new Map(
				slide,
				{
					image: this.slideData.image,
					map: this.slideData.map,
					labels: this.slideData.mapLabels
				},
				{ x: 0, y: 0, w: presentationDimensions.w, h: presentationDimensions.h }
			);

			map.create();
		}
		// text overlay
		if (this.slideData.headline) {
			textArray.push({ text: this.slideData.headline, options: { ...h3, breakLine: true } });
		}

		if (this.slideData.pisteLength) {
			upperMetaDataText.push(`${this.i18n.pisteLength}: ${global.formatDistance(this.slideData.pisteLength)}`);
		}

		if (this.slideData.lifts) {
			upperMetaDataText.push(`${this.i18n.lifts}: ${this.slideData.lifts}`);
		}

		if (this.slideData.sledTrackLength) {
			upperMetaDataText.push(`${this.i18n.sledTrackLength}: ${global.formatDistance(this.slideData.sledTrackLength)}`);
		}

		if (this.slideData.winterHikeLength) {
			upperMetaDataText.push(
				`${this.i18n.winterHikeLength}: ${global.formatDistance(this.slideData.winterHikeLength)}`
			);
		}

		if (this.slideData.elevationMin && this.slideData.elevationMax) {
			lowerMetaDataText.push(
				`${this.i18n.elevation}: ${global.formatElevationRange({
					elevationMin: this.slideData.elevationMin,
					elevationMax: this.slideData.elevationMax
				})}`
			);
		}

		const operatingTimes = this.mapOperatingTimes(this.slideData.operatingTimes);

		if (operatingTimes) {
			lowerMetaDataText.push(`${this.i18n.operatingTime}: ${operatingTimes}`);
		}

		if (upperMetaDataText.length) {
			textArray.push({
				text: upperMetaDataText.join('  ●  '),
				options: { ...copyBold, breakLine: true }
			});
		}

		if (lowerMetaDataText.length) {
			textArray.push({
				text: lowerMetaDataText.join('  ●  '),
				options: { ...copySmall, breakLine: false }
			});
		}

		slide.addText(textArray, {
			shape: 'rect',
			x: 0,
			y: presentationDimensions.h - overlayHeight,
			w: presentationDimensions.w,
			h: overlayHeight,
			color: colors.blue,
			fill: { color: colors.white, transparency: 35 },
			align: 'center',
			margin: [50, 50, 4, 0]
		});

		if (this.slideData.badgeText) {
			createBadgeForSlide(slide, this.slideData.badgeText, {
				x:
					presentationDimensions.w -
					getBadgeWidthFromText(this.slideData.badgeText) -
					defaultViewPortPadding.horizontal,
				y: presentationDimensions.h - overlayHeight - elementSizes.badgeHeight / 2
			});
		}

		createLogoForSlide(slide);
	}
}
